import React, { useState } from "react";
import axios from "axios";
import { FaStar } from "react-icons/fa";
import { USER_URL } from "../../constant/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function CustomerReview({ isOpen, onClose, orderId }) {
  const [review, setReview] = useState({ rating: 0, comment: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReviewSubmit = async (e) => {
    // Ripple effect
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    // Validation
    if (!review.rating || !review.comment) {
      toast.error("Please provide both a rating and a comment.");
      return;
    }

    setIsSubmitting(true); // Start loading

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("You are not logged in. Please log in to submit a review.");
        return;
      }

      await USER_URL.post(`/products/reviews/`, {
        product_id: orderId,
        rating: review.rating,
        comments: review.comment,
      });

      toast.success("Review submitted successfully!");
      setReview({ rating: 0, comment: "" }); // Reset form
      onClose(); // Close popup
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(error.response?.data?.error || "An error occurred while submitting the review.");
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  if (!isOpen) return null; // Don't render if the popup is not open

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Toast Container */}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          onClick={onClose}
        >
          ✕
        </button>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 text-center text-green-600">Add Review</h2>

        {/* Star Rating */}
        <p className="font-bold mb-2 ml-2">Rate product</p>
        <div className="flex mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <FaStar
              key={star}
              className={`cursor-pointer size-10 w-14 ${
                review.rating >= star ? "text-yellow-400" : "text-gray-300"
              } transition-colors duration-200`}
              onClick={() => setReview({ ...review, rating: star })}
            />
          ))}
        </div>

        {/* Comment Input */}
        <textarea
          placeholder="Write your review here..."
          className="border w-full p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
          rows="5"
          value={review.comment}
          onChange={(e) => setReview({ ...review, comment: e.target.value })}
        ></textarea>

        {/* Submit Button */}
        <button
          onClick={handleReviewSubmit}
          disabled={isSubmitting}
          className="mt-4 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded w-full transition-all duration-300 hover:from-green-600 hover:to-green-700 active:scale-95 shadow-lg hover:shadow-xl relative overflow-hidden disabled:opacity-75 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span className="ml-2">Submitting...</span>
            </div>
          ) : (
            "Submit"
          )}
        </button>
      </div>

      {/* Ripple Effect CSS */}
      <style>
        {`
          .ripple {
            position: absolute;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.7);
            transform: scale(0);
            animation: ripple-animation 600ms linear;
          }

          @keyframes ripple-animation {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default CustomerReview;